import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { IMaskDirective } from 'angular-imask';
import IMask from 'imask';
import { ButtonComponent, GroupButtonComponent } from '../../button';
import { InputBase } from '../input-base/input-base';
import { DOMEvent, InputClasses, ValidClasses } from '../input-base/input-base.model';
import { InputErrorsComponent } from '../input-base/input-errors/input-errors.component';
import { InputLabelComponent } from '../input-base/input-label/input-label.component';
import { InputPreAppendComponent } from '../input-base/input-pre-append/input-pre-append.component';

export type MaskedOptions = IMask.AnyMaskedOptions;
export type MaskElement = IMask.MaskElement;

@Component({
  selector: 'app-number,mbs-number',
  imports: [
    ButtonComponent,
    GroupButtonComponent,
    IMaskDirective,
    InputErrorsComponent,
    InputLabelComponent,
    InputPreAppendComponent,
    NgClass
  ],
  templateUrl: './number.component.html'
})
export class NumberComponent extends InputBase<number> implements OnInit, ControlValueAccessor {
  protected cd = inject(ChangeDetectorRef);

  @Input() public max: number = Number.MAX_SAFE_INTEGER;
  @Input() public min: number = Number.MIN_SAFE_INTEGER;
  @Input() public step = 1;
  @Input() imaskOpts?: MaskedOptions;
  /**
   * HTML tabindex
   */
  @Input() tabindex = 0;
  /**
   * Toggles showing '+' and '-' buttons
   */
  @Input() showButtons = true;
  /**
   * Add font-weight-bold;
   */
  @Input() public boldLabel = true;

  @Input() public containerClasses = '';

  @ViewChild(IMaskDirective, { read: IMaskDirective, static: true }) maskDirective: IMaskDirective<MaskedOptions>;

  /**
   * @ignore
   */
  public get bindClasses(): string[] {
    const classesObject: InputClasses = Object.assign<Record<string, boolean>, ValidClasses>({}, this.validClasses);
    return Object.entries(classesObject)
      .filter(([k, v]) => !!v)
      .map(([k]) => k)
      .concat([this.sizeClass]);
  }
  public elementSelectors = {
    id: {
      btnUp: 'mbs-button-up',
      btnDown: 'mbs-button-down'
    }
  };

  protected labelContentClassesDefault = 'mbs-number_label-content';

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.maskDirective.writeValue(String(this.myValue));
  }

  writeValue(obj: any): void {
    this.myValue = obj != null ? +obj : 0;
    this.maskDirective.writeValue(String(this.myValue));
    this.cd.markForCheck();
  }

  changeValueHandler(isIncrement = true): void {
    this.checkRanges(isIncrement);
    this.onTouched && this.onTouched();
    this.maskDirective.writeValue(String(this.value));
  }

  numberChangeHandler(event: Event): void {
    event.stopPropagation();
  }

  checkRanges(isIncrement: boolean): void {
    if (isIncrement) {
      this.value = this.max && +this.value + +this.step > this.max ? +this.max : +this.value + +this.step;
    } else {
      this.value = this.min && this.value - +this.step < this.min ? +this.min : +this.value - +this.step;
    }
  }

  get calculatedContainerClasses(): string {
    return this.containerClasses ? `${this.containerClasses} input-group` : 'input-group';
  }

  numberHandleBlur(event: DOMEvent<HTMLInputElement>): void {
    if (!event?.target) {
      return this.handleBlur(event);
    }

    if (this.imaskOpts) {
      const num = +this.value;
      const clamped = isNaN(num) ? this.min || 0 : num < this.min ? +this.min : num > this.max ? +this.max : num;

      this.value = clamped;
      this.maskDirective.writeValue(String(clamped));

      return this.handleBlur(event);
    }

    const num = Number(event.target.value);
    if (this.imaskOpts == null && isNaN(num)) {
      const newValue = this.min || 0;
      this.value = newValue;
      event.target.value = String(newValue);

      return this.handleBlur(event);
    }

    if (num < this.min) {
      event.preventDefault();
      event.target.value = String(this.min);
      this.value = this.min;
    } else if (num > this.max) {
      event.preventDefault();
      event.target.value = String(this.max);
      this.value = this.max;
    } else {
      this.value = num;
    }

    this.handleBlur(event);
  }

  updateMaskValue(event: DOMEvent<HTMLInputElement> = null): void {
    setTimeout(() => {
      // Clamping min while typing (or rewriting the mask on every keystroke) makes it impossible to clear the field
      // or type through an intermediate value below min — min is enforced on blur instead
      if (!isNaN(+this.value) && this.max && this.value > this.max) {
        this.value = +this.max;
        this.maskDirective.writeValue(String(this.value));
      }
    });
  }

  handleInput(event: DOMEvent<HTMLInputElement>): void {
    if (this.imaskOpts) {
      this.updateMaskValue(event);
      return;
    }
    // if number
    if (/^-?(\d+(\.|(\.\d+)?))?$/.test(event.target.value)) {
      const num = Number(event.target.value);
      this.value = num > this.max ? this.max : num < this.min ? this.min : num;

      return;
    }

    event.preventDefault();
    event.target.value = String(this.myValue);
  }

  handleAccept(event): void {
    this.value = +event;
  }
}
